/**
 * Ski Crash Animation - Snow explosion + tumble effect
 * Combines snow particles with tumbling skier animation
 */

interface SnowParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
  type: 'snow' | 'ski' | 'pole';
}

interface TumbleState {
  x: number;
  y: number;
  rotation: number;
  rotationSpeed: number;
  vx: number;
  vy: number;
  bounces: number;
}

export class Explosion {
  private x: number = 0;
  private y: number = 0;
  private particles: SnowParticle[] = [];
  private tumble: TumbleState | null = null;
  private isActive: boolean = false;
  private frameCount: number = 0;
  private maxFrames: number = 90; // 1.5 seconds at 60fps


  /**
   * Trigger snow crash at given position
   */
  trigger(x: number, y: number, skierTilt: number = 0) {
    this.x = x;
    this.y = y;
    this.isActive = true;
    this.frameCount = 0;
    this.particles = [];

    // Initialize tumbling skier
    this.tumble = {
      x: x,
      y: y,
      rotation: skierTilt * 2,
      rotationSpeed: 0.3 + Math.random() * 0.2,
      vx: skierTilt * 3,
      vy: -4,
      bounces: 0
    };

    // Create snow explosion particles
    const snowCount = 40;
    for (let i = 0; i < snowCount; i++) {
      const angle = (Math.PI * 2 * i) / snowCount + Math.random() * 0.5;
      const speed = 1.5 + Math.random() * 4;

      this.particles.push({
        x: this.x + (Math.random() - 0.5) * 30,
        y: this.y + (Math.random() - 0.5) * 20,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2, // Initial upward burst
        size: 2 + Math.random() * 6,
        opacity: 0.7 + Math.random() * 0.3,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        type: 'snow'
      });
    }

    // Add flying ski equipment
    for (let i = 0; i < 2; i++) {
      this.particles.push({
        x: this.x + (i === 0 ? -15 : 15),
        y: this.y,
        vx: (i === 0 ? -3 : 3) + (Math.random() - 0.5) * 2,
        vy: -5 - Math.random() * 3,
        size: 15,
        opacity: 1,
        rotation: Math.random() * Math.PI,
        rotationSpeed: (Math.random() - 0.5) * 0.4,
        type: 'ski'
      });
    }

    // Add flying poles
    for (let i = 0; i < 2; i++) {
      this.particles.push({
        x: this.x + (i === 0 ? -8 : 8),
        y: this.y - 10,
        vx: (i === 0 ? -2 : 2) + (Math.random() - 0.5),
        vy: -6 - Math.random() * 2,
        size: 20,
        opacity: 1,
        rotation: Math.random() * Math.PI,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        type: 'pole'
      });
    }
  }

  /**
   * Update crash animation
   */
  update() {
    if (!this.isActive) return;

    this.frameCount++;

    // Update tumbling skier
    if (this.tumble) {
      this.tumble.x += this.tumble.vx;
      this.tumble.y += this.tumble.vy;
      this.tumble.vy += 0.25; // Gravity
      this.tumble.rotation += this.tumble.rotationSpeed;

      // Bounce when hitting ground level
      if (this.tumble.y > this.y + 30 && this.tumble.bounces < 2) {
        this.tumble.vy *= -0.4;
        this.tumble.vx *= 0.7;
        this.tumble.rotationSpeed *= 0.6;
        this.tumble.bounces++;

        // Spawn more snow on bounce
        for (let i = 0; i < 8; i++) {
          this.particles.push({
            x: this.tumble.x,
            y: this.tumble.y,
            vx: (Math.random() - 0.5) * 3,
            vy: -Math.random() * 2,
            size: 2 + Math.random() * 3,
            opacity: 0.5,
            rotation: 0,
            rotationSpeed: 0,
            type: 'snow'
          });
        }
      }

      // Slow down after bounces
      if (this.tumble.bounces >= 2) {
        this.tumble.vx *= 0.95;
        this.tumble.rotationSpeed *= 0.95;
      }
    }

    // Update particles
    this.particles.forEach(particle => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += 0.15; // Gravity (lighter for snow)
      particle.rotation += particle.rotationSpeed;

      if (particle.type === 'snow') {
        particle.opacity -= 0.012;
        particle.size *= 0.98;
      } else {
        particle.opacity -= 0.008;
      }
    });

    // Remove dead particles
    this.particles = this.particles.filter(p => p.opacity > 0.05);

    // Deactivate when complete
    if (this.frameCount >= this.maxFrames) {
      this.isActive = false;
    }
  }

  /**
   * Draw crash animation
   */
  draw(ctx: CanvasRenderingContext2D) {
    if (!this.isActive) return;

    // Draw initial snow burst flash
    if (this.frameCount < 8) {
      ctx.save();
      ctx.globalAlpha = (1 - this.frameCount / 8) * 0.4;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(this.x, this.y, 30 + this.frameCount * 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Draw snow particles
    this.particles.forEach(particle => {
      ctx.save();
      ctx.globalAlpha = particle.opacity;
      ctx.translate(particle.x, particle.y);
      ctx.rotate(particle.rotation);

      if (particle.type === 'snow') {
        // Snow chunk
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = 'rgba(200, 220, 240, 0.5)';
        ctx.shadowBlur = 3;
        ctx.beginPath();
        ctx.arc(0, 0, particle.size, 0, Math.PI * 2);
        ctx.fill();
      } else if (particle.type === 'ski') {
        // Flying ski
        ctx.fillStyle = '#4a90d9';
        ctx.fillRect(-particle.size, -2, particle.size * 2, 4);
        ctx.fillStyle = '#2563eb';
        ctx.fillRect(-particle.size + 2, -1, particle.size * 2 - 4, 2);
      } else if (particle.type === 'pole') {
        // Flying pole
        ctx.strokeStyle = '#666666';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -particle.size / 2);
        ctx.lineTo(0, particle.size / 2);
        ctx.stroke();
        // Pole basket
        ctx.fillStyle = '#888888';
        ctx.beginPath();
        ctx.arc(0, particle.size / 2 - 3, 4, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    });

    // Draw tumbling skier
    if (this.tumble && this.frameCount < 70) {
      ctx.save();
      ctx.translate(this.tumble.x, this.tumble.y);
      ctx.rotate(this.tumble.rotation);

      // Fading as animation progresses
      ctx.globalAlpha = Math.max(0, 1 - this.frameCount / 70);

      // Draw tumbling skier emoji (slightly smaller and rotated)
      ctx.font = '35px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⛷️', 0, 0);

      ctx.restore();
    }

    // Draw "crash" impact text briefly
    if (this.frameCount > 10 && this.frameCount < 40) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, (40 - this.frameCount) / 20);
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ff4444';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.strokeText('💥', this.x, this.y - 50 - (this.frameCount - 10) * 0.5);
      ctx.fillText('💥', this.x, this.y - 50 - (this.frameCount - 10) * 0.5);
      ctx.restore();
    }
  }

  /**
   * Check if animation is playing
   */
  isPlaying(): boolean {
    return this.isActive;
  }

  /**
   * Reset animation state
   */
  reset() {
    this.isActive = false;
    this.particles = [];
    this.tumble = null;
    this.frameCount = 0;
  }
}
